import React from "react";
import { Loader2 } from "lucide-react";
import {
  formatMoneyValue,
  getAdjustmentTypeLabel,
  getDirectionLabel,
  getEvidenceLabel,
} from "./contractAdjustmentsUtils";
import type { AttachedEvidence } from "./useContractAdjustmentEvidence";
import type { FormData } from "./useContractAdjustmentWizard";
import type { ContractCeilingView } from "./contractAdjustmentsUtils";

export type ContractAdjustmentReviewStepProps = {
  formData: FormData;
  ceiling: ContractCeilingView | null;
  attachedEvidence: AttachedEvidence[];
  isSubmitting: boolean;
  canSubmit: boolean;
  isEdit: boolean;
  readEvidenceFromBackend: () => Promise<AttachedEvidence[]>;
};

export function ContractAdjustmentReviewStep({
  formData,
  ceiling,
  attachedEvidence,
  isSubmitting,
  canSubmit,
  isEdit: _isEdit,
  readEvidenceFromBackend: _readEvidenceFromBackend,
}: ContractAdjustmentReviewStepProps) {
  return (
    <div className="grid gap-4 py-2">
      <h4 className="font-medium">Revisión de la modificación</h4>

      <div className="grid gap-3 text-sm md:grid-cols-2">
        <div className="rounded-md bg-muted/40 p-3">
          <div className="text-xs font-medium uppercase text-muted-foreground">Título</div>
          <div className="mt-1">{formData.title}</div>
        </div>

        <div className="rounded-md bg-muted/40 p-3">
          <div className="text-xs font-medium uppercase text-muted-foreground">Tipo</div>
          <div className="mt-1">{getAdjustmentTypeLabel(formData.adjustmentType)}</div>
        </div>

        <div className="rounded-md bg-muted/40 p-3">
          <div className="text-xs font-medium uppercase text-muted-foreground">Dirección</div>
          <div className="mt-1">{getDirectionLabel(formData.direction)}</div>
        </div>

        <div className="rounded-md bg-muted/40 p-3">
          <div className="text-xs font-medium uppercase text-muted-foreground">Importe</div>
          <div className="mt-1 font-medium">
            {formatMoneyValue(formData.amount, {
              signed: formData.direction === "increase",
            })}
          </div>
        </div>

        <div className="rounded-md bg-muted/40 p-3">
          <div className="text-xs font-medium uppercase text-muted-foreground">Fecha efectiva</div>
          <div className="mt-1">
            {formData.effectiveDate
              ? new Date(formData.effectiveDate).toLocaleDateString("es-ES")
              : "—"}
          </div>
        </div>

        <div className="rounded-md bg-muted/40 p-3">
          <div className="text-xs font-medium uppercase text-muted-foreground">Evidencias</div>
          <div className="mt-1">{getEvidenceLabel(attachedEvidence.length)}</div>
        </div>

        <div className="rounded-md bg-muted/40 p-3 md:col-span-2">
          <div className="text-xs font-medium uppercase text-muted-foreground">Motivo</div>
          <div className="mt-1">{formData.reason}</div>
        </div>
      </div>

      {/* Ceiling preview — read-only, no mutation */}
      {ceiling && (
        <div className="rounded-md border p-3">
          <h5 className="mb-2 text-sm font-medium">Previsualización del techo contractual</h5>
          <div className="grid gap-2 text-sm md:grid-cols-2">
            <div>
              <span className="text-muted-foreground">Techo vigente:</span>{" "}
              <span className="font-medium">
                {formatMoneyValue(
                  ceiling.effectiveContractCeilingCents ?? ceiling.effectiveContractCeiling,
                )}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Importe de esta modificación:</span>{" "}
              <span className="font-medium">
                {formatMoneyValue(formData.amount, { signed: true })}
              </span>
            </div>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            El techo contractual no cambiará hasta que la modificación sea aprobada.
          </p>
        </div>
      )}

      {/* Submit warning */}
      {!canSubmit && attachedEvidence.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          No se puede enviar a aprobación: todas las evidencias son de tipo "Presupuesto" o "Otro".
          Vuelve al paso anterior y añade al menos una evidencia cualificante
          (anexo firmado, contrato firmado, pedido o aceptación por correo).
        </div>
      )}

      {isSubmitting && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Enviando modificación...
        </div>
      )}
    </div>
  );
}